import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Select, Spin, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Contact, RoleType, ContactService } from '../../services/contact.api.client';
import { useCompany } from '../../contexts/CompanyContext';
import { useConfig } from '../../contexts/ConfigContext';
import { UnifiedContactModal } from '@ryo/ui';
import { useContact } from '../../contexts/ContactContext';

interface ContactSearchSelectProps {
    role: RoleType;
    placeholder: string;
    mode?: 'multiple' | 'tags';
    value?: string | string[];
    onChange?: (value: any) => void;
    initialOptions?: { uid: string, name: string, id: number }[];
    onSelectContact?: (contact: Contact) => void;
    onSelectContacts?: (contacts: Contact[]) => void;
}

export const ContactSearchSelect: React.FC<ContactSearchSelectProps> = ({
    role,
    placeholder,
    mode,
    value,
    onChange,
    initialOptions = [],
    onSelectContact,
    onSelectContacts
}) => {
    const { company } = useCompany();
    const { config } = useConfig();
    const { createContact } = useContact();
    const [fetching, setFetching] = useState(false);
    const [data, setData] = useState<Contact[]>([]);
    const [initialContacts, setInitialContacts] = useState<Contact[]>([]);
    const [currentOptions, setCurrentOptions] = useState<Contact[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    const handleChange = (val: any) => {
        if (onChange) onChange(val);
        
        if (mode === 'multiple' && onSelectContacts) {
            const selectedUids = Array.isArray(val) ? val : [val];
            const selectedContacts = data.filter(c => selectedUids.includes(c.uid));
            onSelectContacts(selectedContacts);
        } else if (onSelectContact) {
            const selected = data.find(c => c.uid === val);
            if (selected) onSelectContact(selected);
        }
    };

    useEffect(() => {
        const fetchInitialSuggestions = async () => {
            if (!company?.current?.uid || !config.key) return;
            setFetching(true);
            try {
                const response = await ContactService.getBulkContacts(role, company.current.uid, config.key);
                if (response?.contactList) {
                    const results = response.contactList;
                    setInitialContacts(results);
                    setCurrentOptions(results);
                    setData(prev => {
                        const existingUids = new Set(prev.map(c => c.uid));
                        const newResults = results.filter((c: any) => !existingUids.has(c.uid));
                        return [...prev, ...newResults];
                    });
                }
            } catch (error) {
                console.error('Error fetching initial contacts:', error);
            } finally {
                setFetching(false);
            }
        };

        fetchInitialSuggestions();
    }, [company?.current?.uid, config.key, role]);

    useEffect(() => {
        if (initialOptions.length > 0) {
            setData(prev => {
                const existingUids = new Set(prev.map(c => c.uid));
                const newItems = initialOptions
                    .filter(opt => !existingUids.has(opt.uid))
                    .map(opt => ({
                        uid: opt.uid,
                        name: opt.name,
                        id: opt.id,
                        role: role
                    } as Contact));
                return [...prev, ...newItems];
            });
        }
    }, [initialOptions, role]);

    const fetchOptions = useCallback((query: string) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (!query || query.trim() === '') {
            setCurrentOptions(initialContacts);
            return;
        }

        timeoutRef.current = setTimeout(async () => {
            if (!company?.current?.uid || !config.key) return;

            setFetching(true);
            try {
                const response = await ContactService.searchBulkContacts(query, role, company.current.uid, config.key);
                if (response?.contactList) {
                    const results = response.contactList;
                    setCurrentOptions(results); 
                    setData(prev => {
                        const existingUids = new Set(prev.map(c => c.uid));
                        const newResults = results.filter((c: any) => !existingUids.has(c.uid));
                        return [...prev, ...newResults];
                    });
                }
            } catch (error) {
                console.error('Error searching contacts:', error);
            } finally {
                setFetching(false);
            }
        }, 800);
    }, [company?.current?.uid, config.key, role, initialContacts]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const options = useMemo(() => {
        const currentUids = new Set(currentOptions.map(c => c.uid));
        const selectedValuesArray = Array.isArray(value) ? value : (value ? [value] : []);
        const optionsToShow = [...currentOptions];
        
        selectedValuesArray.forEach(val => {
            if (!currentUids.has(val)) {
                const cachedContact = data.find(c => c.uid === val);
                if (cachedContact) {
                    optionsToShow.push(cachedContact);
                    currentUids.add(cachedContact.uid);
                }
            }
        });

        return optionsToShow.map(item => ({
            label: item.name,
            value: item.uid,
        }));
    }, [currentOptions, data, value]);

    const handleSaveContact = async (values: any) => {
        setModalLoading(true);
        try {
            const contactData = {
                ...values,
                roles: [{ role_type: role }]
            };
            const res = await createContact(contactData);
            if (res?.message || res?.err) {
                throw new Error(res?.message || res?.err || 'Error al crear el contacto');
            }
            
            const newContact = res as unknown as Contact;
            if (newContact?.uid) {
                setData(prev => {
                    const exists = prev.some(c => c.uid === newContact.uid);
                    const updatedData = exists ? prev : [...prev, newContact];
                    
                    setTimeout(() => {
                        if (mode === 'multiple') {
                            const currentVals = Array.isArray(value) ? value : (value ? [value] : []);
                            const newVals = [...currentVals, newContact.uid];
                            if (onChange) onChange(newVals);
                            if (onSelectContacts) {
                                const selectedContacts = updatedData.filter(c => newVals.includes(c.uid));
                                onSelectContacts(selectedContacts);
                            }
                        } else {
                            if (onChange) onChange(newContact.uid);
                            if (onSelectContact) {
                                onSelectContact(newContact);
                            }
                        }
                    }, 0);
                    
                    return updatedData;
                });
            }
            setIsModalVisible(false);
        } finally {
            setModalLoading(false);
        }
    };

    return (
        <>
            <Select
                showSearch
                mode={mode}
                value={value}
                placeholder={placeholder}
                notFoundContent={fetching ? <Spin size="small" /> : <span>No se encontraron resultados</span>}
                filterOption={false}
                onSearch={fetchOptions}
                onChange={handleChange}
                style={{ width: '100%' }}
                options={options}
                dropdownRender={(menu) => (
                    <>
                        {fetching ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                                <Spin size="small" />
                            </div>
                        ) : (
                            menu
                        )}
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setIsModalVisible(true)}
                                size="small"
                                style={{ width: '100%' }}
                            >
                                Crear {role === 'EMPLOYEE' ? 'Empleado' : 'Cliente'}
                            </Button>
                        </div>
                    </>
                )}
            />
            <UnifiedContactModal
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onSuccess={() => {}}
                role={role as any}
                validationMode="relaxed"
                onSave={handleSaveContact}
                loading={modalLoading}
            />
        </>
    );
};
